import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const logout = () => {
    localStorage.clear();
    setToken(null);
    setUser(null);
  };

  if (!token) {
    return <Auth setToken={setToken} setUser={setUser} />;
  }

  if (user?.role === 'admin') {
    return <AdminPanel user={user} logout={logout} />;
  }

  return <ExaminerPortal user={user} logout={logout} />;
}

// ===== AUTH COMPONENT =====
function Auth({ setToken, setUser }) {
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', password: '', pin: '' });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const url = isLogin ? `${API}/login` : `${API}/register`;
      const res = await axios.post(url, form);
      const { token, user } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setToken(token);
      setUser(user);
    } catch (e) {
      alert(e.response?.data?.error || 'An error occurred');
    }
  };

  return (
    <div className="auth">
      <div className="auth-header">
        <h1>Global Academic Hub</h1>
        <p>Portal</p>
      </div>
      <h2>{isLogin ? 'Login' : 'Register'}</h2>
      <form onSubmit={submit}>
        {!isLogin && (
          <>
            <input
              placeholder="Full name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
            />
            <input
              placeholder="15-Character PIN (from Admin)"
              value={form.pin}
              onChange={e => setForm({ ...form, pin: e.target.value.toUpperCase() })}
              maxLength={15}
              required
              style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
            />
          </>
        )}
        <input
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={form.password}
          onChange={e => setForm({ ...form, password: e.target.value })}
          required
        />
        <button type="submit">{isLogin ? 'Login' : 'Register'}</button>
      </form>
      <button className="toggle-btn" onClick={() => setIsLogin(!isLogin)}>
        {isLogin ? 'Create new account' : 'Already have an account? Login'}
      </button>
    </div>
  );
}

// ===== EXAMINER PORTAL =====
function ExaminerPortal({ user, logout }) {
  return (
    <div className="app">
      <header className="app-header">
        <div>
          <h1>Global Academic Hub (GAH)</h1>
          <p className="subtitle">Professional Education Platform</p>
        </div>
        <div>
          <span className="user-info">Welcome, {user?.name}</span>
          <button onClick={logout}>Logout</button>
        </div>
      </header>
      <main>
        <Uploader />
        <MyUploads />
      </main>
      <footer className="app-footer">
        <p>© 2024 Global Academic Hub | Excellence – Our Tradition, Excellence – Our Gene</p>
        <p>Contact: globalacademichubadm@gmail.com</p>
      </footer>
    </div>
  );
}

// ===== UPLOADER COMPONENT =====
function Uploader() {
  const [files, setFiles] = useState([]);
  const [subject, setSubject] = useState('');
  const [examType, setExamType] = useState('JAMB');
  const [fileFormat, setFileFormat] = useState('Excel');
  const [uploadMode, setUploadMode] = useState('single');
  const [yearFrom, setYearFrom] = useState('1978');
  const [yearTo, setYearTo] = useState(new Date().getFullYear().toString());
  const [fileYears, setFileYears] = useState([]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    
    if (uploadMode === 'multiple') {
      setFileYears(selectedFiles.map(() => new Date().getFullYear().toString()));
    }
  };

  const updateFileYear = (index, year) => {
    const newYears = [...fileYears];
    newYears[index] = year;
    setFileYears(newYears);
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newYears = fileYears.filter((_, i) => i !== index);
    setFiles(newFiles);
    setFileYears(newYears);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return alert('Please select at least one file');

    const fd = new FormData();
    
    files.forEach(file => {
      fd.append('files', file);
    });
    
    fd.append('subject', subject);
    fd.append('exam_type', examType);
    fd.append('file_format', fileFormat);
    
    if (uploadMode === 'single') {
      fd.append('year_from', yearFrom);
      fd.append('year_to', yearTo);
    } else {
      fd.append('years', JSON.stringify(fileYears));
    }

    try {
      const res = await axios.post(`${API}/upload`, fd);
      alert(`✅ ${res.data.count} file(s) uploaded successfully!`);
      setFiles([]);
      setFileYears([]);
      setSubject('');
      document.querySelector('input[type="file"]').value = '';
      window.location.reload();
    } catch (e) {
      alert(e.response?.data?.error || 'Upload failed');
    }
  };

  return (
    <section className="uploader">
      <h3>📤 Upload Questions</h3>
      
      <div className="upload-mode-toggle">
        <button
          type="button"
          className={uploadMode === 'single' ? 'active' : ''}
          onClick={() => setUploadMode('single')}
        >
          📄 Single File (Year Range)
        </button>
        <button
          type="button"
          className={uploadMode === 'multiple' ? 'active' : ''}
          onClick={() => setUploadMode('multiple')}
        >
          📚 Multiple Files (Individual Years)
        </button>
      </div>

      <form onSubmit={submit}>
        <input
          type="text"
          placeholder="Subject (e.g., Physics, Mathematics)"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          required
        />
        
        <select value={examType} onChange={e => setExamType(e.target.value)}>
          <option>JAMB</option>
          <option>WAEC</option>
          <option>NECO</option>
          <option>IGCSE</option>
          <option>GCE</option>
          <option>NABTEB</option>
          <option>POST-UTME</option>
          <option>OTHERS</option>
        </select>
        
        <select value={fileFormat} onChange={e => setFileFormat(e.target.value)}>
          <option>Excel (.xlsx, .xls)</option>
          <option>Word (.docx, .doc)</option>
          <option>CSV (.csv)</option>
          <option>PDF (.pdf)</option>
        </select>

        {uploadMode === 'single' && (
          <div className="years">
            <input
              type="number"
              placeholder="From Year"
              value={yearFrom}
              onChange={e => setYearFrom(e.target.value)}
            />
            <input
              type="number"
              placeholder="To Year"
              value={yearTo}
              onChange={e => setYearTo(e.target.value)}
            />
          </div>
        )}

        <input
          type="file"
          accept=".xls,.xlsx,.csv,.doc,.docx,.pdf"
          onChange={handleFileChange}
          multiple={uploadMode === 'multiple'}
          required
        />

        {uploadMode === 'multiple' && files.length > 0 && (
          <div className="files-list">
            <h4>📎 Selected Files ({files.length}):</h4>
            {files.map((file, index) => (
              <div key={index} className="file-item">
                <span className="file-name">📄 {file.name}</span>
                <input
                  type="number"
                  placeholder="Year"
                  value={fileYears[index] || ''}
                  onChange={e => updateFileYear(index, e.target.value)}
                  className="file-year-input"
                  required
                />
                <button
                  type="button"
                  className="remove-file-btn"
                  onClick={() => removeFile(index)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <button type="submit">
          {uploadMode === 'single' ? '📤 Upload File' : `📤 Upload ${files.length} File(s)`}
        </button>
      </form>
    </section>
  );
}

// ===== MY UPLOADS COMPONENT =====
function MyUploads() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    axios.get(`${API}/my-uploads`)
      .then(r => setRows(r.data))
      .catch(() => {});
  }, []);

  return (
    <section className="myuploads">
      <h3>📊 My Uploads</h3>
      {rows.length === 0 ? (
        <p className="no-data">No uploads yet. Start by uploading your first file above.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Exam</th>
              <th>Format</th>
              <th>Years</th>
              <th>File</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.subject}</td>
                <td>{r.exam_type}</td>
                <td>{r.file_format}</td>
                <td>{r.year_from === r.year_to ? r.year_from : `${r.year_from} - ${r.year_to}`}</td>
                <td>
                  <a href={`/uploads/${r.filename}`} target="_blank" rel="noopener noreferrer">
                    {r.originalname}
                  </a>
                </td>
                <td>
                  <span className={`status status-${r.status}`}>{r.status}</span>
                </td>
                <td>{new Date(r.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ===== ADMIN PANEL =====
function AdminPanel({ user, logout }) {
  const [view, setView] = useState('pins');
  
  return (
    <div className="admin-app">
      <header className="admin-header">
        <div>
          <h1>🛡️ Global Academic Hub - Admin Panel</h1>
          <p>Review and Manage System</p>
        </div>
        <div>
          <span className="admin-badge">ADMIN</span>
          <span className="user-info">{user?.name}</span>
          <button onClick={logout}>Logout</button>
        </div>
      </header>
      
      <nav className="admin-nav">
        <button className={view === 'pins' ? 'active' : ''} onClick={() => setView('pins')}>
          🔑 PIN Management
        </button>
        <button className={view === 'uploads' ? 'active' : ''} onClick={() => setView('uploads')}>
          📋 Review Uploads
        </button>
        <button className={view === 'stats' ? 'active' : ''} onClick={() => setView('stats')}>
          📊 Statistics
        </button>
      </nav>
      
      <main className="admin-main">
        {view === 'pins' && <PINManagement />}
        {view === 'uploads' && <UploadReview />}
        {view === 'stats' && <Statistics />}
      </main>
      
      <footer className="app-footer">
        <p>© 2024 Global Academic Hub - Admin Portal</p>
        <p>Contact: globalacademichubadm@gmail.com</p>
      </footer>
    </div>
  );
}

// ===== PIN MANAGEMENT =====
function PINManagement() {
  const [pins, setPins] = useState([]);
  const [email, setEmail] = useState('');
  const [count, setCount] = useState(1);
  const [generatedPins, setGeneratedPins] = useState([]);

  useEffect(() => {
    loadPins();
  }, []);

  const loadPins = () => {
    axios.get(`${API}/admin/pins`).then(r => setPins(r.data)).catch(e => console.error(e));
  };

  const generatePins = async () => {
    try {
      const res = await axios.post(`${API}/admin/generate-pin`, { email: email || null, count });
      setGeneratedPins(res.data.pins);
      loadPins();
      alert(`${count} PIN(s) generated successfully!`);
    } catch (e) {
      alert('Failed to generate PINs');
    }
  };

  const deletePIN = async (pin) => {
    if (!window.confirm('Delete this PIN?')) return;
    await axios.delete(`${API}/admin/pin/${pin}`);
    loadPins();
  };

  return (
    <div className="pin-management">
      <section className="generate-pin-section">
        <h3>🔑 Generate New PIN</h3>
        <div className="pin-form">
          <input
            type="email"
            placeholder="Assign to Email (optional)"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <input
            type="number"
            placeholder="Number of PINs"
            value={count}
            min="1"
            max="50"
            onChange={e => setCount(e.target.value)}
          />
          <button onClick={generatePins}>Generate PIN(s)</button>
        </div>
        
        {generatedPins.length > 0 && (
          <div className="generated-pins">
            <h4>✅ Generated PINs (Copy these!):</h4>
            {generatedPins.map((pin, i) => (
              <div key={i} className="pin-display">
                <code>{pin}</code>
                <button onClick={() => navigator.clipboard.writeText(pin)}>Copy</button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="pins-list">
        <h3>📋 All PINs ({pins.length})</h3>
        <table>
          <thead>
            <tr>
              <th>PIN</th>
              <th>Assigned Email</th>
              <th>Status</th>
              <th>Created</th>
              <th>Used At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pins.map(pin => (
              <tr key={pin.id}>
                <td><code>{pin.pin}</code></td>
                <td>{pin.email || 'Unassigned'}</td>
                <td>
                  <span className={`status status-${pin.used ? 'approved' : 'pending'}`}>
                    {pin.used ? 'Used' : 'Unused'}
                  </span>
                </td>
                <td>{new Date(pin.created_at).toLocaleDateString()}</td>
                <td>{pin.used_at ? new Date(pin.used_at).toLocaleDateString() : '-'}</td>
                <td>
                  {!pin.used && (
                    <button className="delete-btn" onClick={() => deletePIN(pin.pin)}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ===== UPLOAD REVIEW =====
function UploadReview() {
  const [uploads, setUploads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadUploads();
  }, []);

  const loadUploads = () => {
    axios.get(`${API}/uploads`).then(r => setUploads(r.data)).catch(e => console.error(e));
  };

  const handleReview = (upload) => {
    setSelected(upload);
    setStatus(upload.status);
    setNotes(upload.notes || '');
  };

  const submitReview = async () => {
    await axios.post(`${API}/uploads/${selected.id}/review`, { status, notes });
    alert('Review submitted!');
    setSelected(null);
    loadUploads();
  };

  return (
    <div className="upload-review">
      <h3>📋 All Submissions ({uploads.length})</h3>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Examiner</th>
            <th>Subject</th>
            <th>Exam</th>
            <th>Format</th>
            <th>Years</th>
            <th>File</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {uploads.map(u => (
            <tr key={u.id}>
              <td>{u.id}</td>
              <td>
                <div>{u.examiner_name}</div>
                <div style={{fontSize: '11px', color: '#666'}}>{u.examiner_email}</div>
              </td>
              <td>{u.subject}</td>
              <td>{u.exam_type}</td>
              <td>{u.file_format}</td>
              <td>{u.year_from === u.year_to ? u.year_from : `${u.year_from}-${u.year_to}`}</td>
              <td>
                <a href={`/uploads/${u.filename}`} target="_blank" rel="noopener noreferrer">
                  📥 Download
                </a>
              </td>
              <td>
                <span className={`status status-${u.status}`}>{u.status}</span>
              </td>
              <td>{new Date(u.created_at).toLocaleDateString()}</td>
              <td>
                <button onClick={() => handleReview(u)}>Review</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected && (
        <div className="review-modal">
          <div className="modal-content">
            <h3>Review Submission</h3>
            <p><strong>Examiner:</strong> {selected.examiner_name}</p>
            <p><strong>Subject:</strong> {selected.subject}</p>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add notes..."
              rows="4"
            />
            <div className="modal-actions">
              <button onClick={submitReview}>Submit Review</button>
              <button onClick={() => setSelected(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== STATISTICS =====
function Statistics() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`)
      .then(r => setStats(r.data))
      .catch(e => {
        console.error('Stats error:', e);
        alert('Failed to load statistics');
      });
  }, []);

  if (!stats) return <div>Loading...</div>;

  return (
    <div className="statistics">
      <h3>📊 System Statistics</h3>
      <div className="stats-grid">
        <StatCard title="Total Uploads" value={stats.uploads.total} color="#1976d2" icon="📊" />
        <StatCard title="Pending Review" value={stats.uploads.pending} color="#ff9800" icon="⏳" />
        <StatCard title="Total Examiners" value={stats.examiners} color="#9c27b0" icon="👥" />
        <StatCard title="Unused PINs" value={stats.unusedPins} color="#00bcd4" icon="🔑" />
        <StatCard title="Total Questions" value={stats.questions.total} color="#4caf50" icon="❓" />
        <StatCard title="Students" value={stats.students} color="#e91e63" icon="🎓" />
      </div>
    </div>
  );
}

function StatCard({ title, value, color, icon }) {
  return (
    <div className="stat-card" style={{ borderColor: color }}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-title">{title}</div>
    </div>
  );
}

export default App;